import {
  Error,
  List,
  RecordContextProvider,
  useGetList,
  useGetRecordId,
  useListContext,
  useRecordContext,
} from "react-admin";
import { alpha, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Column } from "../../misc/Column";
import { CardsGrid } from "../../misc/CardsGrid";
import { Instructor } from "../../providers/instructorsProvider";
import { Branch } from "../../providers/branchesProvider";
import { MultiSelectInput } from "../../inputs/MultiSelectInput";
import { Row } from "../../misc/Row";
import { ActiveStudentsField } from "../../fields/ActiveStudentsField";
import { StudentsIcon } from "../../icons/StudentsIcon";
import { overviewCardStyle } from "../../misc/backofficeTheme";
import { useNavigate } from "react-router";
import { currentUser, grants } from "../../backoffice.access_control";
import { EmptyState } from "../../misc/EmptyState";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { InstructorMobileListItem } from "../../lists/InstructorMobileListItem";
import { SearchFilterInput } from "../../inputs/SearchFilterInput";
import { EmployeesListSkeleton } from "../../lists/EmployeesListSkeleton";
import { EmployeeAvatar } from "../../misc/EmployeeAvatar";

/** See https://zpl.io/dRxNOMl */
export function DrivingSchoolInstructors(props: {
  showInactive?: boolean;
  surfaceVariant?: "compactNarrowTabContent" | "compactNarrowStandalone";
}) {
  const { showInactive = false, surfaceVariant = "compactNarrowTabContent" } = props;
  const drivingSchoolId = useGetRecordId();
  const {
    data: branches,
    error: branchesError,
    refetch: refetchBranches,
    isFetching,
  } = useGetList<Branch>(
    "branches",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );

  if (!drivingSchoolId || !branches) {
    const retryBranches = () => {
      void runSafely(() => refetchBranches({ throwOnError: true }), {
        errorMessage: "Die Filialen konnten nicht erneut geladen werden.",
      });
    };
    return (
      <ResponsiveListSurface variant={surfaceVariant} data-testid="DrivingSchoolInstructors">
        {branchesError ? (
          <>
            <Error error={branchesError} resetErrorBoundary={retryBranches} />
            <Row sx={{ justifyContent: "center", mt: 2 }}>
              <LoadingButton
                variant="outlined"
                startIcon={<RefreshIcon />}
                loading={isFetching}
                onClick={retryBranches}
              >
                Erneut laden
              </LoadingButton>
            </Row>
          </>
        ) : (
          <EmployeesListSkeleton variant="full" />
        )}
      </ResponsiveListSurface>
    );
  }

  const hasBranchFilter = branches.length > 1;
  const filters = [
    <SearchFilterInput key="q" source="q" alwaysOn resettable />,
    ...(hasBranchFilter
      ? [
          <MultiSelectInput
            key="assignedBranches"
            label="Filiale"
            source="assignedBranches"
            variant="text"
            withMode
            choices={[{ id: "!", name: "Keine Filiale zugewiesen" }, ...branches.map(({ id, name }) => ({ id, name }))]}
            alwaysOn
          />,
        ]
      : []),
  ];

  return (
    <ResponsiveListSurface variant={surfaceVariant} data-testid="DrivingSchoolInstructors">
      <List
        className="cards-list"
        resource="instructors"
        title=" " // <-- prevent that the default list title is rendered
        storeKey={`DrivingSchoolInstructors-${showInactive ? "inactive" : "active"}@${drivingSchoolId}`}
        filter={{
          drivingSchoolId,
          ...(grants.has("Employee:list") || grants.has("Instructor:list") ? {} : { id: currentUser?.uid }),
          isActive: !showInactive,
        }}
        filters={filters}
        actions={false}
        sort={{ field: "_sortOrder", order: "ASC" }}
        perPage={9999}
        pagination={false}
        disableSyncWithLocation
        exporter={false}
        empty={
          <EmptyState
            noEntriesText="Noch kein Fahrlehrer vorhanden."
            noMatchingEntriesText="Keine passenden Fahrlehrer gefunden."
          />
        }
        sx={{
          boxShadow: "none",
          marginLeft: "auto",
          "& .RaList-content": { boxShadow: "none", border: "none", borderRadius: 0 },
          "& .RaList-actions-topToolbar": {
            justifyContent: "flex-start",
          },
        }}
      >
        <_InstructorCards />
      </List>
    </ResponsiveListSurface>
  );
}

function _InstructorCards() {
  const { data: instructors, isLoading, error, refetch, isFetching } = useListContext<Instructor>();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  if (error && !instructors) {
    const retry = () => {
      void runSafely(async () => refetch({ throwOnError: true }), {
        errorMessage: "Die Fahrlehrer konnten nicht erneut geladen werden.",
      });
    };
    return (
      <>
        <Error error={error} resetErrorBoundary={retry} />
        <Row sx={{ justifyContent: "center", mt: 2 }}>
          <LoadingButton variant="outlined" startIcon={<RefreshIcon />} loading={isFetching} onClick={retry}>
            Erneut laden
          </LoadingButton>
        </Row>
      </>
    );
  }
  if (isLoading && !instructors) {
    return <EmployeesListSkeleton />;
  }
  if (!instructors || instructors.length === 0) {
    return (
      <EmptyState
        noEntriesText="Noch kein Fahrlehrer vorhanden."
        noMatchingEntriesText="Keine passenden Fahrlehrer gefunden."
      />
    );
  }
  if (isMobile) {
    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {instructors.map((instructor) => (
          <InstructorMobileListItem
            key={instructor.id}
            instructor={instructor}
            onClick={() => navigate(`/instructors/${instructor.id}`)}
          />
        ))}
      </Column>
    );
  }
  return (
    <CardsGrid sx={{ mt: "16px", rowGap: "24px", columnGap: "24px" }}>
      {instructors.map((instructor) => (
        <_InstructorCard key={instructor.id} instructor={instructor} />
      ))}
    </CardsGrid>
  );
}

function _InstructorCard({ instructor }: { instructor: Instructor }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const inactiveInstructorSx = instructor.isActive
    ? undefined
    : { "& .MuiTypography-root": { color: "text.disabled" } };

  return (
    <RecordContextProvider value={instructor}>
      <CardsGrid.Card
        onClick={() => navigate(`/instructors/${instructor.id}`)}
        sx={{
          backgroundColor: overviewCardStyle.backgroundColor,
          border: "1px solid",
          borderColor: alpha(theme.palette.text.primary, 0.08),
          boxShadow: "none",
          width: "100%",
          minWidth: "200px",
          "&:hover": {
            borderColor: "text.secondary",
            boxShadow: "none",
            "& .instructor-card-avatar": { transform: "scale(1.07)" },
          },
          "@media (prefers-reduced-motion: reduce)": {
            "& .instructor-card-avatar": { transition: "none", transform: "none" },
            "&:hover .instructor-card-avatar": { transform: "none" },
          },
          ...inactiveInstructorSx,
        }}
      >
        <Row sx={{ justifyContent: "flex-end" }}>
          <_ActiveStudents />
        </Row>
        <Column sx={{ mt: "8px", height: "192px", alignItems: "center" }} gap="8px">
          <EmployeeAvatar
            className="instructor-card-avatar"
            src={instructor.avatarUrl}
            sx={{
              position: "relative",
              top: -2,
              width: 60,
              height: 60,
              transition: "transform 180ms ease-out",
              transformOrigin: "center center",
              willChange: "transform",
            }}
          />
          <_Name />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            {instructor.isDrivingSchoolManager ? "Fahrschulleiter" : "Fahrlehrer"}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ textAlign: "center" }}>
            {instructor?.instructing.drivingLicenseClasses.join(", ")}
          </Typography>
        </Column>
      </CardsGrid.Card>
    </RecordContextProvider>
  );
}

function _Name() {
  const instructor = useRecordContext<Instructor>();
  return (
    <Typography
      sx={{
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "20px",
        textAlign: "center",
      }}
    >
      {instructor.firstName || "\u00A0"}
      <br />
      {instructor.lastName || "\u00A0"}
    </Typography>
  );
}

function _ActiveStudents() {
  return (
    <Row sx={{ color: "text.secondary", height: "20px", justifyContent: "space-between" }} gap="5px">
      <ActiveStudentsField />
      <StudentsIcon sx={{ fill: "currentColor", fontSize: "20px" }} />
    </Row>
  );
}
